/**
 * InviteModal — multi-surface share sheet for every shareable entity type.
 *
 * Covers 7 share types via `InviteModalType`: GUILD, CHALLENGE, STANDARD,
 * TOURNAMENT, TOURNAMENT_TEAM, LEADERBOARD, HUB. Renders one of:
 *
 *   - SMS (sms: link)
 *   - Email (react-share)
 *   - Twitter/X (manual intent URL — text → "👉 Start Here" + URL → hashtags)
 *   - Instagram (clipboard copy + IG deep-link on mobile, since Instagram
 *     has no web share intent API)
 *   - Facebook (FacebookShareModal)
 *   - Native Web Share (mobile only; falls back to GoNative bridge)
 *
 * Two design choices worth knowing:
 *
 *   1. SHARE_BASE_URL = "https://devourplay.gg" — share LINKS use the new
 *      brand domain even though the in-app `VITE_WEB_URL` is still the
 *      legacy `devourgo.io`. Every external click lands on devourplay.gg.
 *
 *   2. AI-suggested copy via `useGenerateShareCopy` — the static
 *      `getSocialQuote()` templates are the fallback; clicking the ✨
 *      button replaces the body copy with a punchier AI variant. The full
 *      composed text is `body \n\n👉 Start Here: <url> \n\n <hashtags>` —
 *      hashtags AFTER the URL (intentional).
 */

import { useGetAsset } from "@/hooks/useAsset";
import {ModalBody} from "@/widgets/ModalParts";
import { useGetGuild } from "@/hooks/useGetGuild";
import { FC, useState } from "react";
import { IoMdClose } from "react-icons/io";
import {Modal} from "@/widgets/Modal";
import { useGetOwnReferralCode } from "@/hooks/useGetOwnReferralCode";
import { useGetActiveReferralProgram } from "@/hooks/useGetActiveReferralProgram";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { FaCheck, FaFacebook, FaInstagram } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdContentCopy, MdEmail } from "react-icons/md";
import { isGoNative } from "@/utils/isGoNative";
import { TbMessageCircleFilled } from "react-icons/tb";
import useThemePreference from "@/hooks/useThemePreference";
import { FiShare } from "react-icons/fi";
import { isMobile } from "react-device-detect";
import { EmailShareButton } from "react-share";
import { FacebookShareModal } from "./FacebookShareModal";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";
import { isColorDarkOrLight } from "@/utils/isColorDarkOrLight";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useGenerateShareCopy } from "@/hooks/useGenerateShareCopy";
import { ShareCopyType } from "@devour/client";

/*
 * Brand domain for SHARE LINKS specifically. Different from `VITE_WEB_URL`
 * which still resolves to the legacy `devourgo.io` for in-app navigation.
 * Share links carry the new brand domain so any social click lands users
 * on the rebranded surface.
 */
const SHARE_BASE_URL = "https://devourplay.gg";

export enum InviteModalType {
    GUILD = "GUILD",
    CHALLENGE = "CHALLENGE",
    STANDARD = "STANDARD",
    TOURNAMENT = "TOURNAMENT", // Share tournament (general promotion)
    TOURNAMENT_TEAM = "TOURNAMENT_TEAM", // Invite to specific team
    LEADERBOARD = "LEADERBOARD", // Share leaderboard competition
    HUB = "HUB", // Invite to a brand hub
}

export interface TournamentShareData {
    id: string;
    name: string;
    gameName?: string;
    /** Prize pool in CENTS. Component divides by 100 before rendering as $X. */
    prizePool?: number;
    /** Entry fee in cents. 0 = free entry (rendered as "FREE entry!"). */
    entryFee?: number;
    backgroundUrl?: string;
}

export interface TeamShareData {
    id: string;
    name: string;
    spotsRemaining?: number;
}

export interface LeaderboardShareData {
    id: string;
    slug?: string;
    name: string;
    gameName?: string;
    /** Raw prize value in whatever unit `currency` describes (points / FUEL / USD). */
    prizeValue?: number;
    /** Unit label for prizeValue (e.g. "points", "FUEL", "USD"). Defaults to "points". */
    currency?: string;
    backgroundUrl?: string;
}

export interface HubShareData {
    slug: string;
    name: string;
    backgroundUrl?: string;
}

interface props {
    type?: InviteModalType;
    guildId?: string;
    challengeId?: string;
    tournamentData?: TournamentShareData;
    teamData?: TeamShareData;
    leaderboardData?: LeaderboardShareData;
    hubData?: HubShareData;
    alwaysUseDarkBackground?: boolean;
    invitePageUrl?: string;
    isVisible: boolean;
    onClose: () => void;
    /** Override brand color (used when challenge.brandColor is empty, e.g., from Hub) */
    brandColorOverride?: string;
}

export const InviteModal: FC<props> = ({ isVisible, onClose, guildId, type = InviteModalType.STANDARD, invitePageUrl = "/", alwaysUseDarkBackground = false, challengeId, tournamentData, teamData, leaderboardData, hubData, brandColorOverride }) => {
    const guild = useGetGuild(guildId);
    const challenge = useGetChallenge(challengeId);
    const guildAssetImage = useGetAsset(guild?.data?.bannerImageLarge || guild?.data?.bannerImageSmall);
    const challengeAssetImage = useGetAsset(challenge?.data?.backgroundImageSmall);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { copyToClipboard } = useCopyToClipboard();
    const [isTextCopied, setIsTextCopied] = useState(false);
    const [isFacebookShareModalVisible, setIsFacebookShareModalVisible] = useState(false);
    const { isOnDarkMode } = useThemePreference();
    const { data: ownReferralCodeData } = useGetOwnReferralCode(fullToken);
    const referralCode = ownReferralCodeData?.referralCodes?.[0]?.code;
    /*
     * Admin-authored referral program copy. Drives the qualifier fine
     * print (and, when we wire it, the callout strings). When the API
     * is loading or no active program exists the qualifier line is
     * suppressed instead of falling back to stale hardcoded text.
     */
    const { data: activeProgram } = useGetActiveReferralProgram();
    /*
     * AI-suggested copy. When the user clicks "✨ Generate with AI", the
     * backend returns a single suggested share post that replaces the
     * default `getSocialQuote()` template. Tracked in state so all share
     * surfaces (Twitter, Instagram copy, SMS, email) pick up the new copy.
     * Cleared when the modal closes via the parent unmount.
     */
    const [aiSuggestedCopy, setAiSuggestedCopy] = useState<string | null>(null);
    const generateShareCopy = useGenerateShareCopy();

    /*
     * Build a `?ref=…` segment ONLY when referralCode is loaded. Without this
     * guard, an unloaded referralCode produced `?ref=undefined` (the literal
     * string), poisoning attribution silently for every share fired before
     * the referral query resolved. Also uses encodeURIComponent (not
     * encodeURI) — the latter does not escape characters legal-in-paths
     * but reserved-in-query, which would corrupt referral codes containing
     * `&` / `=` / `#` / `?`.
     */
    const refQS = (): string => referralCode ? `ref=${encodeURIComponent(referralCode)}` : "";
    const joinQS = (...parts: string[]): string => {
        const filtered = parts.filter(p => p && p.length > 0);
        return filtered.length === 0 ? "" : `?${filtered.join("&")}`;
    };

    const getInviteLink = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return `${SHARE_BASE_URL}/guilds${joinQS(refQS(), `guildId=${guild?.data?.id ?? ""}`)}`;
            case InviteModalType.CHALLENGE: {
                const baseUrl = challenge?.data?.brandMapSlug ? `/brands/${challenge?.data?.brandMapSlug}` : `/challenges/${challengeId}`;
                return `${SHARE_BASE_URL}${baseUrl}${joinQS(refQS(), `challengeId=${challenge?.data?.id ?? ""}`)}`;
            }
            case InviteModalType.TOURNAMENT:
                return `${SHARE_BASE_URL}/play${joinQS("view=tournament", `id=${tournamentData?.id ?? ""}`, refQS())}`;
            case InviteModalType.TOURNAMENT_TEAM:
                return `${SHARE_BASE_URL}/play${joinQS("view=tournament", `id=${tournamentData?.id ?? ""}`, `teamInvite=${teamData?.id ?? ""}`, refQS())}`;
            case InviteModalType.LEADERBOARD:
                return `${SHARE_BASE_URL}/leaderboards/${leaderboardData?.slug || leaderboardData?.id}${joinQS(refQS())}`;
            case InviteModalType.HUB:
                return `${SHARE_BASE_URL}/hub/${hubData?.slug}${joinQS(refQS())}`;
            case InviteModalType.STANDARD:
                return `${SHARE_BASE_URL}${invitePageUrl}${joinQS(refQS())}`;
            default:
                return "";
        }
    };

    const getDescription = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return `Squad up — invite friends to ${guild?.data?.name} and earn referral rewards together.`;
            case InviteModalType.CHALLENGE:
                return `Invite your friends to join the ${challenge?.data?.name}`;
            case InviteModalType.TOURNAMENT: {
                const prizeText = tournamentData?.prizePool ? ` with a $${(tournamentData.prizePool / 100).toLocaleString()} prize pool` : "";
                return `Share the ${tournamentData?.name}${prizeText} and invite your friends to compete!`;
            }
            case InviteModalType.TOURNAMENT_TEAM: {
                const spotsText = teamData?.spotsRemaining ? ` (${teamData.spotsRemaining} spot${teamData.spotsRemaining !== 1 ? "s" : ""} left)` : "";
                return `Invite friends to join ${teamData?.name}${spotsText} in the ${tournamentData?.name}!`;
            }
            case InviteModalType.LEADERBOARD: {
                const prizeText = leaderboardData?.prizeValue ? ` with ${leaderboardData.prizeValue.toLocaleString()} ${leaderboardData.currency || "points"} in prizes` : "";
                return `Share the ${leaderboardData?.name}${prizeText} and invite your friends to compete!`;
            }
            case InviteModalType.HUB:
                return `Invite your friends to join ${hubData?.name || "this hub"} on DevourPlay and earn rewards together!`;
            case InviteModalType.STANDARD:
                return "Invite your friends to join Devour and earn referral rewards for both you and your friend!";
            default:
                return "";
        }
    };

    const getHeroUrl = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return guildAssetImage?.data?.cdnUrl;
            case InviteModalType.CHALLENGE:
                return challengeAssetImage?.data?.cdnUrl;
            case InviteModalType.TOURNAMENT:
            case InviteModalType.TOURNAMENT_TEAM:
                return tournamentData?.backgroundUrl || "";
            case InviteModalType.LEADERBOARD:
                return leaderboardData?.backgroundUrl || "";
            case InviteModalType.HUB:
                return hubData?.backgroundUrl || "";
            case InviteModalType.STANDARD:
                return ""; // Clean background for standard invites
            default:
                return "";
        }
    };

    const getShareMessage = (includeLink = true) => {
        const link = includeLink ? `: ${getInviteLink()}` : "";
        switch (type) {
            case InviteModalType.STANDARD:
                return "👋 Join DevourPlay and Start Earning Rewards";
            case InviteModalType.CHALLENGE:
                return `Join me in the ${challenge?.data?.name} on Devour`;
            case InviteModalType.GUILD:
                return `Join me as I am levelling up my ${guild?.data?.name} guild on DevourPlay! Use my link to sign up now ${link} 🚀`;
            case InviteModalType.HUB:
                return `Join me on ${hubData?.name || "DevourPlay"}! Earn rewards, complete quests, and level up${link} 🎮`;
            case InviteModalType.TOURNAMENT: {
                const prizeText = tournamentData?.prizePool ? ` - $${(tournamentData.prizePool / 100).toLocaleString()} prize pool` : "";
                return `🏆 Check out the ${tournamentData?.name}${prizeText} on DevourPlay! ${link}`;
            }
            case InviteModalType.TOURNAMENT_TEAM:
                return `👥 Join my team "${teamData?.name}" in the ${tournamentData?.name}! ${link}`;
            case InviteModalType.LEADERBOARD: {
                const prizeText = leaderboardData?.prizeValue ? ` - ${leaderboardData.prizeValue.toLocaleString()} ${leaderboardData.currency || "points"} in prizes` : "";
                return `🏆 Check out the ${leaderboardData?.name}${prizeText} on DevourPlay! ${link}`;
            }
            default:
                return "";
        }
    };

    const getSocialQuote = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return "I'm using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you're gaming anyway, might as well earn while you do!\n\nCheck it out! 👉";
            case InviteModalType.CHALLENGE:
                return `🎮 Just jumped into the ${challenge?.data?.name} on Devour.\nQuests, instant rewards, & solid prize pool. 🏆\n\nCome run it with me! 😎 👉`;
            case InviteModalType.GUILD:
                return `🎮 I'm repping the ${guild?.data?.name} Guild on Devour!\n\nMonthly prizes for: \n\n🏆 Winning Guild\n🎖️ Top Guild XP earner\n👑 Top XP overall\n\nLet's run it! 💪🎯\n👉 [Join My Guild Here]`;
            case InviteModalType.HUB:
                return `🎮 I'm leveling up on ${hubData?.name || "DevourPlay"}!\n\nQuests, rewards, and exclusive content.\nJoin me and let's earn together! 💪\n👉`;
            case InviteModalType.TOURNAMENT: {
                const gameText = tournamentData?.gameName ? `${tournamentData.gameName} ` : "";
                const prizeText = tournamentData?.prizePool ? `$${(tournamentData.prizePool / 100).toLocaleString()} prize pool` : "prizes up for grabs";
                const entryText = tournamentData?.entryFee === 0 ? "FREE entry" : "";
                return `🏆 ${gameText}Tournament alert!\n\n${tournamentData?.name} on DevourPlay\n💰 ${prizeText}${entryText ? `\n🎟️ ${entryText}` : ""}\n\nRegister now! 👉`;
            }
            case InviteModalType.TOURNAMENT_TEAM: {
                const spotsText = teamData?.spotsRemaining ? `We need ${teamData.spotsRemaining} more player${teamData.spotsRemaining !== 1 ? "s" : ""}!` : "Join the squad!";
                return `👥 Looking for teammates!\n\nJoin "${teamData?.name}" in the ${tournamentData?.name} on DevourPlay.\n\n${spotsText}\n\nClick to join 👉`;
            }
            case InviteModalType.LEADERBOARD: {
                const gameText = leaderboardData?.gameName ? `${leaderboardData.gameName} ` : "";
                const prizeText = leaderboardData?.prizeValue ? `${leaderboardData.prizeValue.toLocaleString()} ${leaderboardData.currency || "points"} in prizes` : "prizes up for grabs";
                return `🏆 ${gameText}Leaderboard competition!\n\n${leaderboardData?.name} on DevourPlay\n💰 ${prizeText}\n\nCompete now! 👉`;
            }
            default:
                return "";
        }
    };

    /*
     * Strip non-alphanumerics so multi-word game names produce valid handles
     * ("Rocket League" -> "#RocketLeague", "Counter-Strike 2" -> "#CounterStrike2")
     */
    const gameHashtag = (name?: string): string | null => {
        if (!name) return null;
        const handle = name.replace(/[^a-zA-Z0-9]/g, "");
        return handle ? `#${handle}` : null;
    };

    const getSocialHashtags = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return [];
            case InviteModalType.CHALLENGE:
                return ["#BrandChallenge", "#GamingRewards"];
            case InviteModalType.GUILD:
                return [];
            case InviteModalType.HUB:
                return ["#DevourPlay", "#Gaming"];
            case InviteModalType.TOURNAMENT:
            case InviteModalType.TOURNAMENT_TEAM: {
                const gameTag = gameHashtag(tournamentData?.gameName);
                return [gameTag, "#DevourPlay", "#Tournament"].filter(Boolean).slice(0, 3) as string[];
            }
            case InviteModalType.LEADERBOARD: {
                const gameTag = gameHashtag(leaderboardData?.gameName);
                return [gameTag, "#DevourPlay", "#Leaderboard"].filter(Boolean).slice(0, 3) as string[];
            }
            default:
                return [];
        }
    };


    const getEmailData = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return {
                    subject: "👋 Join DevourPlay and Start Earning Rewards",
                    body: `I'm using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you're gaming anyway, might as well earn while you do!\n\nCheck it out! 👉 ${getInviteLink()}`,
                };
            case InviteModalType.CHALLENGE:
                return {
                    subject: `Join me in the ${challenge?.data?.name} on Devour`,
                    body: `Hey!\nI just jumped into the ${challenge?.data?.name} on Devour — complete quests, grab instant rewards, and score a shot at some solid prizes 🏆\n\nThought you might want in too. Could be fun to see who finishes it first 😎\n\nHere's the link to join: ${getInviteLink()}`,
                };
            case InviteModalType.GUILD:
                return {
                    subject: `🎮 Squad Up! Join My ${guild?.data?.name} Guild on Devour and Start Earning Rewards`,
                    body: `👋 Hey! Come join me in the ${guild?.data?.name} Guild on Devour — the platform that rewards us just for playing games.\n\nEvery month is a competition, with prizes for:\n🏆 All winning Guild members\n🎖️ The top XP earner in the winning Guild\n👑 The top overall XP earner across all players\n\nLet's squad up and start stacking rewards together. 💪\n\n👉 [Join My Guild Here](${getInviteLink()})\n\nSee you in-game! 🎮`,
                };
            case InviteModalType.HUB:
                return {
                    subject: `🎮 Join me on ${hubData?.name || "DevourPlay"} — earn rewards just for gaming!`,
                    body: `Hey!\n\nI'm on ${hubData?.name || "DevourPlay"} — complete quests, earn XP, and unlock exclusive rewards.\n\nJoin me here: ${getInviteLink()}\n\nSee you in-game! 🎮`,
                };
            case InviteModalType.TOURNAMENT: {
                const gameText = tournamentData?.gameName ? `${tournamentData.gameName} ` : "";
                const prizeText = tournamentData?.prizePool ? `$${(tournamentData.prizePool / 100).toLocaleString()} prize pool` : "prizes available";
                const entryText = tournamentData?.entryFee === 0 ? " • FREE entry!" : "";
                return {
                    subject: `🏆 ${gameText}Tournament: ${tournamentData?.name}`,
                    body: `Hey!\n\nCheck out this tournament on DevourPlay:\n\n${tournamentData?.name}\n💰 ${prizeText}${entryText}\n\nRegister here: ${getInviteLink()}\n\nLet's compete! 🎮`,
                };
            }
            case InviteModalType.TOURNAMENT_TEAM: {
                const spotsText = teamData?.spotsRemaining ? `We need ${teamData.spotsRemaining} more player${teamData.spotsRemaining !== 1 ? "s" : ""} to complete our roster.` : "We'd love to have you on the team.";
                return {
                    subject: `👥 Join my team in ${tournamentData?.name}!`,
                    body: `Hey!\n\nI'm putting together a team for the ${tournamentData?.name} on DevourPlay.\n\nTeam: ${teamData?.name}\n${spotsText}\n\nClick here to join: ${getInviteLink()}\n\nLet's win this! 🏆`,
                };
            }
            case InviteModalType.LEADERBOARD: {
                const gameText = leaderboardData?.gameName ? `${leaderboardData.gameName} ` : "";
                const prizeText = leaderboardData?.prizeValue ? `${leaderboardData.prizeValue.toLocaleString()} ${leaderboardData.currency || "points"} in prizes` : "prizes available";
                return {
                    subject: `🏆 ${gameText}Leaderboard: ${leaderboardData?.name}`,
                    body: `Hey!\n\nCheck out this leaderboard on DevourPlay:\n\n${leaderboardData?.name}\n💰 ${prizeText}\n\nJoin here: ${getInviteLink()}\n\nLet's compete! 🎮`,
                };
            }
            default:
                return { subject: "", body: "" };
        }
    };

    const getSmsBody = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return `I'm using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you're gaming anyway, might as well earn while you do!\n\nCheck it out! 👉 ${getInviteLink()}`;
            case InviteModalType.CHALLENGE:
                return `I just joined the ${challenge?.data?.name} on Devour 🎮\nQuests, instant rewards, and a shot at the prize pool 🏆\nCome run it with me 😎 ${getInviteLink()}`;
            case InviteModalType.GUILD:
                return `👋 Hey! Join me in the ${guild?.data?.name} Guild on Devour.\nWe earn rewards just for gaming + monthly prizes too.\nHop in here:\n${getInviteLink()}`;
            case InviteModalType.HUB:
                return `🎮 Join me on ${hubData?.name || "DevourPlay"}! Quests, rewards, and exclusive content.\n${getInviteLink()}`;
            case InviteModalType.TOURNAMENT: {
                const prizeText = tournamentData?.prizePool ? `$${(tournamentData.prizePool / 100).toLocaleString()} prize pool` : "prizes";
                return `🏆 ${tournamentData?.name} on DevourPlay!\n${prizeText}\nRegister: ${getInviteLink()}`;
            }
            case InviteModalType.TOURNAMENT_TEAM: {
                const spotsText = teamData?.spotsRemaining ? `Need ${teamData.spotsRemaining} more!` : "";
                return `👥 Join my team "${teamData?.name}" in ${tournamentData?.name}! ${spotsText}\n${getInviteLink()}`;
            }
            case InviteModalType.LEADERBOARD: {
                const prizeText = leaderboardData?.prizeValue ? `${leaderboardData.prizeValue.toLocaleString()} ${leaderboardData.currency || "points"} in prizes` : "prizes";
                return `🏆 ${leaderboardData?.name} on DevourPlay!\n${prizeText}\nJoin: ${getInviteLink()}`;
            }
            default:
                return "";
        }
    };

    // Use challenge brand color or override from Hub
    const effectiveBrandColor = challenge?.data?.brandColor || brandColorOverride;

    const usesBrandColor = type === InviteModalType.CHALLENGE || type === InviteModalType.HUB;

    const getChallengeButtonStyles = (): any => {
        if (usesBrandColor && effectiveBrandColor) {
            const textColor = isColorDarkOrLight(effectiveBrandColor) === "dark" ? "#FFFFFF" : "#282438";
            return {
                backgroundColor: isTextCopied ? undefined : effectiveBrandColor,
                color: isTextCopied ? undefined : textColor,
            };
        }
        return undefined;
    };

    // CSS variables for brand color theming
    const modalStyle = effectiveBrandColor && usesBrandColor
        ? { "--invite-modal-accent": effectiveBrandColor } as React.CSSProperties
        : undefined;


    /*
     * Final share text composed in the order requested by ops:
     *   1. AI/template body copy
     *   2. "👉 Start Here:" CTA + URL
     *   3. Hashtags after URL
     * react-share's TwitterShareButton ordered hashtags BEFORE the URL,
     * which we don't want — so we build the intent URL manually with the
     * full text already pre-composed (and don't pass `url` separately).
     */
    const getFinalShareText = (): string => {
        const body = aiSuggestedCopy || getSocialQuote();
        const url = getInviteLink();
        const hashtags = getSocialHashtags().join(" ");
        const parts = [body, `👉 Start Here: ${url}`];
        if (hashtags) parts.push(hashtags);
        return parts.join("\n\n");
    };

    const handleCopyForInstagram = (): void => {
        copyToClipboard(getFinalShareText());
        setIsTextCopied(true);
        setTimeout(() => setIsTextCopied(false), 4000);
        /*
         * Instagram does not support web-based share intents. Best a web
         * page can do is copy formatted text the user pastes into their
         * IG story / DM / bio. On mobile we *also* deep-link to the IG app
         * so the user lands in the right surface ready to paste.
         */
        if (isMobile) {
            window.open("instagram://camera", "_blank");
        }
    };

    const shareOptions = [
        {
            icon: <TbMessageCircleFilled />,
            title: "Message",
            onClick: () => {
                window.open(`sms:?&body=${encodeURIComponent(getSmsBody())}`);
            },
        },
        {
            icon: <MdEmail />,
            title: "Email",
            render: () => {
                const emailData = getEmailData();
                return (
                    <EmailShareButton
                        url={" "}
                        subject={emailData.subject}
                        body={emailData.body}
                        className="invite-modal_body_content_share-options_option_content"
                        style={{ backgroundColor: "none" }}
                    >
                        <MdEmail />
                    </EmailShareButton>
                );
            },
        },
        {
            icon: <FaXTwitter />,
            title: "X",
            onClick: () => {
                /*
                 * Manual intent URL so we keep the text → URL → hashtags
                 * order. Only `text` is passed; `url` and `hashtags` are
                 * already inside the composed text and Twitter would
                 * otherwise re-append the URL after the body.
                 */
                const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getFinalShareText())}`;
                window.open(intent, "_blank", "noopener,noreferrer");
            },
        },
        {
            icon: <FaInstagram />,
            title: "Insta",
            onClick: handleCopyForInstagram,
        },
        {
            icon: <FaFacebook />,
            title: "Facebook",
            render: () =>
                <div onClick={() => setIsFacebookShareModalVisible(true)} className="invite-modal_body_content_share-options_option_content">
                    <FaFacebook />
                </div>,
        },
        {
            icon: <FiShare />,
            onlyOnMobile: true,
            title: "Share",
            onClick: async () => {
                if (isGoNative()) {
                    // @ts-ignore
                    gonative.share.sharePage({ url: getInviteLink(), text: getShareMessage(false) });
                } else {
                    try {
                        await navigator.share({
                            title: "I am inviting you to DevourPlay!",
                            text: getShareMessage(false),
                            url: getInviteLink(),
                        });
                    } catch (err) {
                        // User cancelled share - this is expected behavior, not an error
                        if (err instanceof Error && err.name === "AbortError") {
                            return;
                        }
                        console.error("Share failed:", err);
                    }
                }
            },
        },
    ];

    const handleCopyLink = () => {
        if (isTextCopied) return;
        setIsTextCopied(true);
        copyToClipboard(getInviteLink());
        setTimeout(() => setIsTextCopied(false), 10000);
    };

    return (
        <>
            <FacebookShareModal
                hashtag={getSocialHashtags().join(" ")}
                onClose={() => setIsFacebookShareModalVisible(false)}
                isVisible={isFacebookShareModalVisible}
                link={getInviteLink()}
                quote={aiSuggestedCopy || getSocialQuote()}
            />
            <Modal size="xs1" isOpen={isVisible} toggle={onClose} contentClassName="invite-modal" style={modalStyle}>
                <button className="invite-modal_close" type="button" aria-label="Close" onClick={onClose}>
                    <IoMdClose />
                </button>
                <ModalBody className="invite-modal_body">
                    {/* Eyebrow + title — anchor `.hd-body` strip */}
                    <div className="invite-modal_body_heading">
                        <h5>{type === InviteModalType.TOURNAMENT ? "Share · Tournament" : type === InviteModalType.TOURNAMENT_TEAM ? "Squad Invite" : type === InviteModalType.LEADERBOARD ? "Share · Leaderboard" : type === InviteModalType.GUILD ? "Squad Invite · Guild" : type === InviteModalType.CHALLENGE ? "Share · Challenge" : type === InviteModalType.HUB ? "Share · Hub" : "Refer-a-Friend"}</h5>
                        <span>{type === InviteModalType.TOURNAMENT ? "Share Tournament." : type === InviteModalType.TOURNAMENT_TEAM ? "Invite to Team." : type === InviteModalType.LEADERBOARD ? "Share Leaderboard." : type === InviteModalType.GUILD ? "Invite to Guild." : type === InviteModalType.CHALLENGE ? "Share Challenge." : type === InviteModalType.HUB ? "Share Hub." : "Invite a Friend."}</span>
                    </div>
                    <div className="invite-modal_body_content">
                        {/* Admin-editable callout — derived from active referral program */}
                        <div className="invite-modal_body_callout">
                            <span className="invite-modal_body_callout-icon">✓</span>
                            <div className="invite-modal_body_callout-copy">
                                {getDescription()}
                            </div>
                        </div>

                        {/* Referral Code + Referral Link fields */}
                        <div className="invite-modal_body_fields">
                            {referralCode &&
                                <div className="invite-modal_body_field-block">
                                    <span className="invite-modal_body_field-block-lbl">Referral Code</span>
                                    <div className="invite-modal_body_field-block-row">
                                        <div className="invite-modal_body_field-block-val">{referralCode}</div>
                                        <button
                                            className={`invite-modal_body_field-block-copy${isTextCopied ? " is-copied" : ""}`}
                                            type="button"
                                            onClick={() => {
                                                copyToClipboard(referralCode);
                                                setIsTextCopied(true);
                                                setTimeout(() => setIsTextCopied(false), 4000);
                                            }}
                                        >
                                            {isTextCopied ? <FaCheck/> : <MdContentCopy/>}
                                            {isTextCopied ? "Copied" : "Copy"}
                                        </button>
                                    </div>
                                </div>
                            }
                            <div className="invite-modal_body_field-block">
                                <span className="invite-modal_body_field-block-lbl">Referral Link</span>
                                <div className="invite-modal_body_field-block-row">
                                    <div className="invite-modal_body_field-block-val">{getInviteLink()}</div>
                                    <button
                                        className={`invite-modal_body_field-block-copy${isTextCopied ? " is-copied" : ""}`}
                                        type="button"
                                        onClick={handleCopyLink}
                                    >
                                        {isTextCopied ? <FaCheck/> : <MdContentCopy/>}
                                        {isTextCopied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <span className="invite-modal_body_share-lbl">Share Via</span>
                        {/*
                         * AI-generated post copy is intentionally hidden —
                         * the backend feature ships ahead of frontend
                         * authorization. Gated `false` until PM sign-off;
                         * when ready replace with the Statsig gate that
                         * unlocks the surface, e.g.
                         *   statsigClient.checkGate("ai_share_copy")
                         * Tracked in the share-copy product spec (no
                         * GitHub issue assigned yet).
                         */}
                        {false &&
                        <div className="invite-modal_body_content_ai">
                            <button
                                type="button"
                                className="invite-modal_body_content_ai-generate"
                                onClick={() => {
                                    /*
                                     * Build a small payload describing what's
                                     * being shared so the AI has the same
                                     * context the templated copy uses today
                                     * (game name, prize, leaderboard/tournament
                                     * title, type). Backend stays in charge of
                                     * tone + length per type.
                                     */
                                    /*
                                     * InviteModalType members map 1:1 to ShareCopyType members
                                     * by string value (both enums use the same underlying strings).
                                     * The cast is TS-level only — runtime values are identical.
                                     */
                                    const payload = {
                                        type: type as unknown as ShareCopyType,
                                        leaderboardName: leaderboardData?.name,
                                        leaderboardGameName: leaderboardData?.gameName,
                                        leaderboardPrizeValue: leaderboardData?.prizeValue,
                                        leaderboardCurrency: leaderboardData?.currency,
                                        tournamentName: tournamentData?.name,
                                        tournamentGameName: tournamentData?.gameName,
                                        tournamentPrizePool: tournamentData?.prizePool,
                                        tournamentEntryFee: tournamentData?.entryFee,
                                        challengeName: challenge?.data?.name,
                                        guildName: guild?.data?.name,
                                        hubName: hubData?.name,
                                    };
                                    generateShareCopy.mutate(payload, {
                                        onSuccess: (res) => setAiSuggestedCopy(res.copy),
                                    });
                                }}
                                disabled={generateShareCopy.isPending}
                            >
                                {generateShareCopy.isPending ? "Generating…" : "✨ Generate post copy with AI"}
                            </button>
                            {aiSuggestedCopy
                                ? <div className="invite-modal_body_content_ai-preview">
                                    <pre>{aiSuggestedCopy}</pre>
                                    <div className="invite-modal_body_content_ai-preview-actions">
                                        <button type="button" onClick={() => setAiSuggestedCopy(null)}>
                                            Use template instead
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => generateShareCopy.mutate({
                                                type: type as unknown as ShareCopyType,
                                                leaderboardName: leaderboardData?.name,
                                                leaderboardGameName: leaderboardData?.gameName,
                                                leaderboardPrizeValue: leaderboardData?.prizeValue,
                                                leaderboardCurrency: leaderboardData?.currency,
                                                tournamentName: tournamentData?.name,
                                                tournamentGameName: tournamentData?.gameName,
                                                tournamentPrizePool: tournamentData?.prizePool,
                                                tournamentEntryFee: tournamentData?.entryFee,
                                                challengeName: challenge?.data?.name,
                                                guildName: guild?.data?.name,
                                                hubName: hubData?.name,
                                            }, {onSuccess: (res) => setAiSuggestedCopy(res.copy)})}
                                            disabled={generateShareCopy.isPending}
                                        >
                                            ↻ Regenerate
                                        </button>
                                    </div>
                                </div>
                                : null}
                        </div>
                        }
                        <div className="invite-modal_body_content_share-options">
                            {shareOptions
                                .filter(option => !(option.onlyOnMobile && !isMobile))
                                .map((option, index) =>
                                    <div
                                        key={index + option.title}
                                        className="invite-modal_body_content_share-options_option"
                                        onClick={option.onClick}
                                        aria-label={`Share via ${option.title}`}
                                    >
                                        {option.render ? option.render()
                                            : <div className="invite-modal_body_content_share-options_option_content">
                                                {option.icon}
                                            </div>
                                        }
                                    </div>)}
                        </div>
                        {/*
                          * Qualifier line — closes out the modal and matches
                          * the anchor `.fine` footer that gives the share
                          * grid its bottom rhythm. Sourced from the active
                          * referral program (admin-editable `qualifierCopy`)
                          * via `useGetActiveReferralProgram`. Suppressed
                          * entirely when the program is paused or the field
                          * is empty so stale text never reads as live terms.
                          */}
                        {type === InviteModalType.STANDARD && activeProgram?.active && activeProgram.qualifierCopy &&
                            <p className="invite-modal_body_fine">
                                * <strong>{activeProgram.qualifierCopy}</strong>
                            </p>
                        }
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};
